import React from "react";
import Tooltip from "./Tooltip";
/* Tab (Segmented) — Phase B primitive.
   Consolidates the 3 legacy systems: <Segmented> + .seg-toggle + .deals-tabs.

   Per IA.md C2: includes "isolate-on-all" multi-select variant for the deals
   channel tabs. Behavior: when ALL options are selected (master "all" state),
   clicking item X isolates to just X. When a subset is selected, clicks toggle
   X in/out additively. Click "all" toggle to reset.

   Props:
     value          — string (single select) OR Array<string> (multi)
     options        — Array<{ id, label, icon?, accent?, disabled?, title? }>
     onChange       — (next) => void
     mode           — "single" (default) | "multi-isolate"
     allOption      — { id, label } — used in multi-isolate as the "all" master toggle
     size           — "sm" (28h) | "md" (32h, default)
     ariaLabel
*/

const { useRef } = React;

export default function Tab({
  value,
  options = [],
  onChange,
  mode = "single",
  allOption = null,
  size = "md",
  ariaLabel,
  className = "",
  ...rest
}) {
  const ref = useRef(null);
  const isMulti = mode === "multi-isolate";
  const selectedIds = isMulti ? (Array.isArray(value) ? value : []) : null;
  const allIds = options.map(o => o.id);
  const allCurrentlyOn = isMulti && allIds.every(id => selectedIds.includes(id));

  const handleSingleClick = (opt) => {
    if (opt.disabled) return;
    if (value !== opt.id) onChange?.(opt.id);
  };
  const handleMultiClick = (opt) => {
    if (opt.disabled) return;
    if (allCurrentlyOn) {
      onChange?.([opt.id]);
      return;
    }
    const next = selectedIds.includes(opt.id)
      ? selectedIds.filter(x => x !== opt.id)
      : [...selectedIds, opt.id];
    onChange?.(next);
  };
  const handleAllClick = () => {
    if (!isMulti) return;
    onChange?.(allCurrentlyOn ? [] : allIds);
  };

  const idx = !isMulti ? options.findIndex(o => o.id === value) : -1;
  const onKey = (e) => {
    if (isMulti) return;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      e.preventDefault();
      const next = options[(idx + 1) % options.length];
      if (next) onChange?.(next.id);
    } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      e.preventDefault();
      const next = options[(idx - 1 + options.length) % options.length];
      if (next) onChange?.(next.id);
    } else if (e.key === "Home") {
      e.preventDefault();
      onChange?.(options[0]?.id);
    } else if (e.key === "End") {
      e.preventDefault();
      onChange?.(options[options.length - 1]?.id);
    }
  };

  const cls = [
    "v2-tab",
    `v2-tab--${size}`,
    isMulti ? "v2-tab--multi" : "v2-tab--single",
    className,
  ].filter(Boolean).join(" ");

  return (
    <div
      ref={ref}
      role={isMulti ? "group" : "radiogroup"}
      aria-label={ariaLabel}
      className={cls}
      onKeyDown={onKey}
      {...rest}
    >
      {isMulti && allOption ? (
        <button
          type="button"
          className={`v2-tab__item v2-tab__item--all ${allCurrentlyOn ? "v2-tab__item--on" : ""}`.trim()}
          onClick={handleAllClick}
          aria-pressed={allCurrentlyOn}
        >
          {allOption.label}
        </button>
      ) : null}
      {options.map(opt => {
        const isOn = isMulti ? selectedIds.includes(opt.id) : value === opt.id;
        const accentClass = opt.accent ? `v2-tab__item--accent-${opt.accent}` : "";
        // Phase 4d (DR-14) — when an option specifies `title`, wrap the
        // button in <Tooltip> instead of putting a native `title=` on it.
        // The inner button keeps all keyboard/click/aria behavior; the
        // wrap-span only intercepts hover/focus to show the tip.
        const btn = (
          <button
            key={opt.id}
            type="button"
            role={isMulti ? undefined : "radio"}
            aria-checked={!isMulti ? isOn : undefined}
            aria-pressed={isMulti ? isOn : undefined}
            aria-disabled={opt.disabled || undefined}
            tabIndex={isMulti ? 0 : (isOn ? 0 : -1)}
            disabled={opt.disabled}
            className={[
              "v2-tab__item",
              isOn ? "v2-tab__item--on" : "",
              accentClass,
            ].filter(Boolean).join(" ")}
            onClick={() => isMulti ? handleMultiClick(opt) : handleSingleClick(opt)}
          >
            {opt.icon != null ? <span className="v2-tab__icon" aria-hidden="true">{opt.icon}</span> : null}
            {opt.label != null ? <span className="v2-tab__label">{opt.label}</span> : null}
          </button>
        );
        return opt.title ? <Tooltip key={opt.id} tip={opt.title}>{btn}</Tooltip> : btn;
      })}
    </div>
  );
}
